import { Head } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Header } from "@/components/header"
import { SampleCard } from "@/components/sample-card"
import { Pagination } from "@/components/pagination"
import { displayName } from "@/lib/utils"
import type User from "@/types/generated/User"
import type Sample from "@/types/generated/Sample"
import type { Pagination as PaginationType } from "@/types"

type Props = {
  user: User
  samples: Sample[]
  pagination: PaginationType
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  })
}

export default function Show({ user, samples, pagination }: Props) {
  const name = displayName(user)

  return (
    <div className="min-h-screen">
      <Head title={name} />
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Profile Header */}
        <div className="flex items-center gap-4 mb-8">
          <Avatar className="h-16 w-16">
            <AvatarImage src={user.avatar_url ?? undefined} />
            <AvatarFallback className="text-xl">{name[0]}</AvatarFallback>
          </Avatar>

          <div className="flex-1">
            <h1 className="text-2xl font-bold">{name}</h1>
            <p className="text-sm text-muted-foreground">
              Joined {formatDate(user.created_at)}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card className="p-4 text-center">
            <div className="text-2xl font-mono font-bold">{user.samples_count}</div>
            <div className="text-sm text-muted-foreground">Samples</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-mono font-bold">{user.votes_received}</div>
            <div className="text-sm text-muted-foreground">Votes Received</div>
          </Card>
        </div>

        {/* Samples */}
        <h2 className="text-lg font-mono font-bold mb-4">SAMPLES</h2>

        {samples.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No samples uploaded yet.
          </p>
        ) : (
          <>
            <div className="space-y-3">
              {samples.map((sample) => (
                <SampleCard key={sample.id} sample={sample} showUser={false} />
              ))}
            </div>

            <Pagination pagination={pagination} baseUrl={`/users/${user.id}`} />
          </>
        )}
      </main>
    </div>
  )
}
